import { useDataValue } from 'Simple/Data.js'
import { useMemo, useState, useEffect } from 'react'
import { storage } from 'Data/helpers.js'
import { STORAGE_KEY } from 'Data/constants.js'

/** @type {import('Simple/types.js').useDataTransform} */
export default function useDataTransform(props, data) {
  let questions = useDataValue({
    context: 'widget',
    path: 'questions',
    viewPath: props.viewPath,
  })

  let id = useDataValue({
    context: 'flow_shortcuts',
    viewPath: props.viewPath,
    path: 'id',
  })

  let preview = useDataValue({
    context: 'flow_shortcuts',
    viewPath: props.viewPath,
    path: 'preview',
  })

  let urlParams = useDataValue({
    context: 'flow_shortcuts',
    viewPath: props.viewPath,
  })

  let [previousState, setPreviousState] = useState(null)

  useEffect(() => {
    let fetchPreviousState = async () => {
      try {
        let storedValue = await storage.getItem(`${STORAGE_KEY}_${id}`)
        if (storedValue && preview !== 'true') {
          setPreviousState(JSON.parse(storedValue))
        } else {
          setPreviousState(null)
        }
      } catch (err) {
        console.error('Error retrieving from storage:', err)
      }
    }

    fetchPreviousState()
  }, [id, preview])

  let prefillAnswers = useMemo(() => {
    if (!urlParams || !questions) return {}

    let answers = {}
    questions.forEach(question => {
      for (let [key, value] of Object.entries(urlParams)) {
        if (
          question.name?.toLowerCase() === key.toLowerCase() ||
          question.id === key
        ) {
          try {
            answers[question.id] = /^{.+}$/.test(value)
              ? JSON.parse(value)
              : value
          } catch {}
          break
        }
      }
    })
    return answers
  }, [urlParams, questions])

  let newState = useMemo(
    () => ({
      ...data,
      flowQuestions: questions
        .filter(item => !item.hidden)
        .map(item => item.id),
      answers: { ...prefillAnswers, ...data.answers },
    }),
    [questions, data, prefillAnswers]
  )

  return useMemo(() => {
    if (!data) return data

    if (previousState) {
      // If we have stored answers for questions that no longer exist, reset
      let answeredQuestionIds = Object.keys(previousState.answers || {})
      let currentIds = new Set(questions.map(q => q.id))
      let hasStaleAnswers = answeredQuestionIds.some(id => !currentIds.has(id))

      if (hasStaleAnswers) {
        return newState
      }
    }

    return {
      ...(previousState ?? newState),
      // Always use fresh flowQuestions (filters hidden questions from current list)
      flowQuestions: newState.flowQuestions,
    }
  }, [data, previousState, newState, questions])
}
